import { User, Mail, Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { TIME_SLOTS, formatDate, fareBreakdown } from '@/components/booking/bookingData';
import { bt, vehClass } from '@/i18n/bookingI18n';

function BookingSummary({ form, getPrice, lang }) {
  return (
    <div data-testid="booking-summary" className="bg-white border border-black/10 rounded-2xl p-5 sticky top-28 shadow-sm">
      <h3 className="font-heading font-bold text-xs text-[#EE5A01] uppercase tracking-[0.2em] mb-5">{bt(lang, 'bookingSummary')}</h3>
      <div className="space-y-3 text-sm">
        {[
          { label: bt(lang, 'sumDuration'), value: form.duration === '4h' ? bt(lang, 'half4h') : bt(lang, 'full8h') },
          { label: bt(lang, 'sumDate'), value: form.date ? formatDate(form.date, lang) : '-' },
          { label: bt(lang, 'sumTime'), value: form.time ? TIME_SLOTS.find((t) => t.value === form.time)?.label : '-' },
          { label: bt(lang, 'sumPickup'), value: form.pickupType === 'airport' ? form.pickupAirport : form.pickupLocation || '-', truncate: true },
          { label: bt(lang, 'sumDropoff'), value: form.dropoffType === 'airport' ? form.dropoffAirport : form.dropoffLocation || '-', truncate: true },
          { label: bt(lang, 'sumVehicle'), value: vehClass(lang, form.vehicle) || '-' },
          { label: bt(lang, 'sumPassengers'), value: form.passengers ? `${lang === 'ar' ? 'حتى' : 'Up to'} ${form.passengers}` : '-' },
        ].map((row) => (
          <div key={row.label} className="flex justify-between">
            <span className="font-body text-[#666666]">{row.label}</span>
            <span className={`font-heading text-[#121212] ${row.truncate ? 'text-right max-w-[140px] truncate' : ''}`}>{row.value}</span>
          </div>
        ))}
        <div className="border-t border-black/10 pt-3 mt-3 space-y-2">
          {(() => {
            const { base, vat, total } = fareBreakdown(getPrice());
            return (
              <>
                <div className="flex justify-between"><span className="font-body text-[#666666]">{bt(lang, 'baseFare')}</span><span className="font-heading text-[#121212]">AED {base.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="font-body text-[#666666]">{bt(lang, 'vat5')}</span><span className="font-heading text-[#121212]">AED {vat.toLocaleString()}</span></div>
                <div className="flex justify-between items-center pt-2 border-t border-black/10">
                  <span className="font-heading font-bold text-[#121212] uppercase text-xs tracking-wider">{bt(lang, 'total')}</span>
                  <span data-testid="summary-total" className="font-heading font-black text-xl text-[#EE5A01]">AED {total.toLocaleString()}</span>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

export function ConfirmStep({ form, errors, set, getPrice, lang = 'en' }) {
  const isAr = lang === 'ar';
  const iconCls = `absolute ${isAr ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-4 h-4 text-[#EE5A01]`;
  const inputCls = `bg-white border-black/15 text-[#121212] placeholder:text-[#999] focus:border-[#EE5A01] focus:ring-[#EE5A01] rounded-lg h-12 ${isAr ? 'pr-10' : 'pl-10'}`;

  return (
    <div data-testid="step-confirm" className="animate-fade-in">
      <h2 className="font-heading font-black text-2xl sm:text-3xl text-[#121212] tracking-tight mb-2 text-center">{bt(lang, 'confirmTitle')}</h2>
      <p className="font-body text-sm text-[#666666] text-center mb-8 sm:mb-10">{bt(lang, 'confirmSub')}</p>

      <div className="max-w-3xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
        <div className="lg:col-span-3 space-y-5">
          <div className="orange-accent-line mb-4" />
          <h3 className="font-heading font-bold text-base text-[#121212] uppercase tracking-wider mb-4">{bt(lang, 'yourDetails')}</h3>
          <div>
            <Label className="font-heading text-xs tracking-wider text-[#121212] uppercase mb-2 block">{bt(lang, 'fullName')}</Label>
            <div className="relative">
              <User className={iconCls} />
              <Input data-testid="input-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder={bt(lang, 'namePh')} className={inputCls} />
            </div>
            {errors.name && <p className="text-[#EE5A01] text-xs mt-1 font-body">{errors.name}</p>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="font-heading text-xs tracking-wider text-[#121212] uppercase mb-2 block">{bt(lang, 'email')}</Label>
              <div className="relative">
                <Mail className={iconCls} />
                <Input data-testid="input-email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder={bt(lang, 'emailPh')} className={inputCls} />
              </div>
              {errors.email && <p className="text-[#EE5A01] text-xs mt-1 font-body">{errors.email}</p>}
            </div>
            <div>
              <Label className="font-heading text-xs tracking-wider text-[#121212] uppercase mb-2 block">{bt(lang, 'phone')}</Label>
              <div className="relative">
                <Phone className={iconCls} />
                <Input data-testid="input-phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder={bt(lang, 'phonePh')} className={inputCls} />
              </div>
              {errors.phone && <p className="text-[#EE5A01] text-xs mt-1 font-body">{errors.phone}</p>}
            </div>
          </div>
          <div>
            <Label className="font-heading text-xs tracking-wider text-[#121212] uppercase mb-2 block">{bt(lang, 'specialRequests')}</Label>
            <Textarea data-testid="input-notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder={bt(lang, 'notesPhConfirm')} className="bg-white border-black/15 text-[#121212] placeholder:text-[#999] focus:border-[#EE5A01] focus:ring-[#EE5A01] rounded-lg min-h-[80px]" />
          </div>
        </div>
        <div className="lg:col-span-2">
          <BookingSummary form={form} getPrice={getPrice} lang={lang} />
        </div>
      </div>
    </div>
  );
}
